// src/screens/ChampionshipScreen.js
// ============================================================================
// PUBLIC CHAMPIONSHIP PAGE (/championship/:slug, plan 3.8 part 2)
//
// Dedicated shareable event page for a championship event. Works logged out,
// calls getChampionshipPage once, then listens to the public event doc so
// sponsors approved later show without a reload (Dennis 11-03). Displays no
// entrant list and no money fields.
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';

import { functions, db } from '../../firebaseConfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { EventSponsorsBlock, openSafeHttpsUrl } from '../components/EventSponsorsBlock';
import { setPageMeta, resetPageMeta } from '../utils/pageMeta';
import { subscribeChampionshipEvent } from '../utils/championshipPage';

function formatLineFor(format) {
  if (!format || typeof format !== 'string') return null;
  const norm = format.trim().toLowerCase().replace(/[\s_-]+/g, '_');
  if (!norm || norm === 'single' || norm === 'single_elimination') return null;
  if (norm === 'double' || norm === 'double_elimination') return 'Double elimination';
  return format.trim();
}

export default function ChampionshipScreen({
  navigation,
  route,
  initialData = null,
  initialNotFound = false,
}) {
  const slug = route?.params?.slug ? String(route.params.slug).trim().toLowerCase() : '';
  const [pageData, setPageData] = useState(initialData);
  const [loading, setLoading] = useState(!initialData && !initialNotFound);
  const [notFound, setNotFound] = useState(Boolean(initialNotFound));

  if (initialData?.name) {
    setPageMeta({ title: initialData.name });
  }

  useEffect(() => {
    if (initialData?.name) {
      setPageMeta({ title: initialData.name });
    }
    return () => {
      resetPageMeta();
    };
  }, [initialData]);

  useEffect(() => {
    if (initialData || initialNotFound) return undefined;
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    let unsubscribe = null;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const call = httpsCallable(functions, 'getChampionshipPage');
        const res = await call({ slug });
        if (cancelled) return;
        const data = res?.data || null;
        if (!data || !data.tournamentId || !data.championship) {
          setNotFound(true);
        } else {
          setPageData(data);
          if (data.name) {
            setPageMeta({ title: data.name });
          }
          unsubscribe = subscribeChampionshipEvent(
            { db, doc, onSnapshot },
            data.tournamentId,
            slug,
            (fresh) => { if (!cancelled) setPageData(fresh); },
            () => { if (!cancelled) setNotFound(true); },
          );
        }
      } catch (_) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [slug, initialData, initialNotFound]);

  if (loading) {
    return (
      <SafeAreaView style={styles.safe}>
        <Header navigation={navigation} />
        <View style={styles.centerBox}>
          <ActivityIndicator size="large" color="#FCD000" />
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  if (notFound || !pageData || !pageData.championship) {
    return (
      <SafeAreaView style={styles.safe}>
        <Header navigation={navigation} />
        <View style={styles.centerBox}>
          <Ionicons name="calendar-outline" size={40} color="#8b92a8" />
          <Text style={styles.notFoundTitle}>This event page is not available.</Text>
          <TouchableOpacity
            style={styles.primaryBtn}
            onPress={() => navigation?.navigate?.('Lobby')}
          >
            <Text style={styles.primaryBtnText}>Events</Text>
          </TouchableOpacity>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  const organizerName = pageData.organizer?.name ? String(pageData.organizer.name).trim() : '';
  const organizerUrl = pageData.organizer?.url ? String(pageData.organizer.url).trim() : '';
  const stages = Array.isArray(pageData.championship?.stages) ? pageData.championship.stages : [];
  const venueName = pageData.championship?.venue?.name ? String(pageData.championship.venue.name).trim() : '';
  const venueCity = pageData.championship?.venue?.city ? String(pageData.championship.venue.city).trim() : '';
  const formatLine = formatLineFor(pageData.format);
  const capacity = Math.max(0, Math.floor(Number(pageData.maxEntrants) || 0));
  const rulesText = pageData.championship?.rulesText ? String(pageData.championship.rulesText) : '';
  const canSponsor = pageData.branded === true && pageData.sponsorSignupOpen === true;

  return (
    <SafeAreaView style={styles.safe}>
      <Header navigation={navigation} />
      <ScrollView style={styles.scroll} contentContainerStyle={styles.container}>
        {/* 1. Official event header */}
        <View style={styles.heroCard}>
          <Text style={styles.eyebrow}>Official event</Text>
          <Text style={styles.eventName}>{pageData.name || 'Event'}</Text>
          {Boolean(organizerName) && (
            organizerUrl ? (
              <TouchableOpacity onPress={() => openSafeHttpsUrl(organizerUrl)} activeOpacity={0.8}>
                <Text style={[styles.organizerLine, styles.organizerLink]}>
                  {`Organized by ${organizerName}`}
                </Text>
              </TouchableOpacity>
            ) : (
              <Text style={styles.organizerLine}>{`Organized by ${organizerName}`}</Text>
            )
          )}
        </View>

        {/* 2. When and where: stages, then venue */}
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>When and where</Text>
          <View style={styles.stageList}>
            {stages.map((st, idx) => (
              <View key={`stage_${idx}`} style={styles.stageRow}>
                <Text style={styles.stageName}>{st.name}</Text>
                <Text style={styles.stageMeta}>{`${st.when} | ${st.where}`}</Text>
              </View>
            ))}
          </View>
          {Boolean(venueName || venueCity) && (
            <View style={styles.venueBox}>
              <Text style={styles.venueName}>{venueName}</Text>
              {Boolean(venueCity) && <Text style={styles.venueCity}>{venueCity}</Text>}
            </View>
          )}
        </View>

        {/* 3. Format line (when present) and capacity */}
        <View style={styles.card}>
          {Boolean(formatLine) && (
            <Text style={styles.formatLine}>{formatLine}</Text>
          )}
          {capacity > 0 && (
            <Text style={styles.capacityLine}>{`Up to ${capacity} players`}</Text>
          )}
        </View>

        {/* 4. EventSponsorsBlock (renders nothing unless branded === true) */}
        <EventSponsorsBlock ev={pageData} />

        {/* 5. Rules (plain text with line breaks, never HTML or markdown) */}
        {Boolean(rulesText) && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>Rules</Text>
            <Text style={styles.rulesBody}>{rulesText}</Text>
          </View>
        )}

        {/* 6. 18+ notice */}
        <Text style={styles.ageLine}>Players must be 18 or older.</Text>

        {/* 7. Action buttons */}
        <View style={styles.actionsRow}>
          <TouchableOpacity
            style={styles.primaryBtn}
            onPress={() =>
              navigation?.navigate?.('EventDetailScreen', {
                eventId: pageData.tournamentId,
              })
            }
          >
            <Text style={styles.primaryBtnText}>Sign up</Text>
          </TouchableOpacity>

          {canSponsor && (
            <TouchableOpacity
              style={styles.secondaryBtn}
              onPress={() =>
                navigation?.navigate?.('Sponsor', {
                  event: pageData.tournamentId,
                })
              }
            >
              <Text style={styles.secondaryBtnText}>Sponsor this event</Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: '#0b0d17',
  },
  scroll: {
    flex: 1,
  },
  container: {
    maxWidth: 860,
    width: '100%',
    alignSelf: 'center',
    padding: 20,
    gap: 16,
  },
  centerBox: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 14,
  },
  notFoundTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
  },
  heroCard: {
    backgroundColor: '#121526',
    borderWidth: 1,
    borderColor: '#FCD000',
    borderRadius: 12,
    padding: 20,
    gap: 8,
  },
  eyebrow: {
    color: '#FCD000',
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  eventName: {
    color: '#FFFFFF',
    fontSize: 26,
    fontWeight: '900',
  },
  organizerLine: {
    color: '#c9d1d9',
    fontSize: 14,
    fontWeight: '700',
  },
  organizerLink: {
    color: '#FCD000',
    textDecorationLine: 'underline',
  },
  card: {
    backgroundColor: '#121526',
    borderWidth: 1,
    borderColor: '#232842',
    borderRadius: 12,
    padding: 18,
    gap: 10,
  },
  sectionTitle: {
    color: '#FFFFFF',
    fontSize: 17,
    fontWeight: '800',
  },
  stageList: {
    gap: 10,
  },
  stageRow: {
    backgroundColor: '#161a2e',
    borderWidth: 1,
    borderColor: '#252a48',
    borderRadius: 8,
    padding: 12,
    gap: 4,
  },
  stageName: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '800',
  },
  stageMeta: {
    color: '#9aa0b4',
    fontSize: 13,
  },
  venueBox: {
    borderTopWidth: 1,
    borderTopColor: '#232842',
    paddingTop: 10,
    gap: 2,
  },
  venueName: {
    color: '#E7ECFF',
    fontSize: 14,
    fontWeight: '800',
  },
  venueCity: {
    color: '#9aa0b4',
    fontSize: 13,
  },
  formatLine: {
    color: '#FCD000',
    fontSize: 15,
    fontWeight: '800',
  },
  capacityLine: {
    color: '#E7ECFF',
    fontSize: 14,
    fontWeight: '700',
  },
  rulesBody: {
    color: '#c9d1d9',
    fontSize: 14,
    lineHeight: 21,
  },
  ageLine: {
    color: '#9aa0b4',
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'center',
  },
  actionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
  },
  primaryBtn: {
    backgroundColor: '#FCD000',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 22,
    alignItems: 'center',
  },
  primaryBtnText: {
    color: '#000000',
    fontSize: 15,
    fontWeight: '800',
  },
  secondaryBtn: {
    backgroundColor: '#1a1e35',
    borderWidth: 1,
    borderColor: '#FCD000',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 22,
    alignItems: 'center',
  },
  secondaryBtnText: {
    color: '#FCD000',
    fontSize: 15,
    fontWeight: '800',
  },
});

